<template>
  <error-page v-if="isNotFound"
              :error="{ statusCode: 404 }" />
  <div v-else
       id="products"
       class="grid-wrapper products"
       v-resize="handleResize">

    <loader :loading="this.loading"/>

    <default-error v-if="error"/>

    <ais-instant-search v-if="searchProducts"
                        v-bind="instantSearchParams"
                        :key="`${$route.fullPath}-${instantSearchRenderKey}`"
                        class="product-list"
                        :data-insights-product-index="indexName"
                        :middlewares="instantSearchMiddlewares">

      <ais-configure :hitsPerPage="60"
                     :filters="defaultFilters"
                     :disjunctiveFacets="disjunctiveFacets"
                     :facetingAfterDistinct="facetingAfterDistinct"
                     :enableRules="true"
                     :ruleContexts="['all']" />

      <br>

      <products-listing-header :category="productsListingHeaderCategory"
                               :categories="categories"
                               :index-name="indexName"
                               class="products-listing-header"/>

      <div class="product-list-main">
        <div id="filter-options-wrapper">
          <sticky-top-bar parent-container-selector=".grid-wrapper.products"
                          :offset="filtersOffset"
                          :active="!mobile"
                          :z-index="15">
            <filter-options :index-name="indexName"
                            :alternative-filters="alternativeFilters"
                            :filters="filters"
                            :ui-data="uiData"
                            :categories="categories"
                            @applyFilters="applyFilters" />
          </sticky-top-bar>
        </div>

        <product-list @productsChanged="triggerFunctionsAfterMounting"/>
      </div>

    </ais-instant-search>

    <lazy-hydrate never>
      <product-footer v-if="!!supplier.footer_description"
                      :description="supplier.footer_description"/>
    </lazy-hydrate>

  </div>

</template>

<script>
  import ApiProduct from '@/api/Product'

  import { compact } from 'lodash'

  import { AisInstantSearch, AisConfigure } from 'vue-instantsearch'
  import AlgoliaInsightsMiddelwareMixin from '@/mixins/algoliaInsightsMiddleware'
  import AlgoliaProductsInstantSearchMixin from '@/mixins/algoliaProductsInstantSearch'
  import AlgoliaProductsRoutingMixin from '@/mixins/algoliaProductsRouting'
  import AlternativeFiltersMixin from '@/mixins/alternativeFilters'
  import CordialMixin from '@/mixins/cordial'
  import DynamicProductCategoryMixin from '@/mixins/dynamicProductCategory'
  import ProductsCategoriesLoadableMixin from '@/mixins/productsCategoriesLoadable'
  import ProductsFiltersLoadableMixin from '@/mixins/productsFiltersLoadable'
  import ProductsListingResponsive from '@/mixins/products/listing/productsListingResponsive'
  import ProductsListMetadata from '@/mixins/productsListMetadata'
  import StickyFiltersMixin from '@/mixins/stickySideFilters'

  import BrandBanner from '@/components/BrandBanner'
  import DefaultError from '@/components/DefaultError'
  import ErrorPage from '@/layouts/error'
  import FilterList from '@/components/FilterList'
  import FilterOptions from '@/components/FilterOptions'
  import LazyHydrate from 'vue-lazy-hydration'
  import Loader from '@/components/Loader'
  import Pagination from '@/../shared/components/Pagination'
  import ProductFooter from '@/components/ProductFooter'
  import ProductList from '@/components/ProductList'
  import ProductsListingHeader from '@/components/products/listing/ProductsListingHeader'
  import StickyTopBar from '@/../shared/components/StickyTopBar'


  export default {
    name: 'brand-view',
    mixins: [
      AlgoliaInsightsMiddelwareMixin,
      AlgoliaProductsInstantSearchMixin,
      AlgoliaProductsRoutingMixin,
      AlternativeFiltersMixin,
      CordialMixin,
      ProductsCategoriesLoadableMixin,
      // "ProductsFiltersLoadableMixin" mixin must be declared before "DynamicProductCategoryMixin"
      // because it overrides the calculated property "isDynamicCategoryPage"
      ProductsFiltersLoadableMixin,
      DynamicProductCategoryMixin,
      ProductsListingResponsive,
      ProductsListMetadata,
      StickyFiltersMixin
    ],
    components: {
      AisConfigure,
      AisInstantSearch,
      BrandBanner,
      DefaultError,
      ErrorPage,
      FilterList,
      FilterOptions,
      LazyHydrate,
      Loader,
      Pagination,
      ProductFooter,
      ProductList,
      ProductsListingHeader,
      StickyTopBar
    },
    head () {
      return {
      title: this.getMetaTitle,
      link: [
        {
        rel: 'canonical',
        href: `${this.$config.baseUrl}${this.$route.path}`
        }
      ],
      meta: [
        {
        name: 'description',
        content: this.getMetaDescription
        }
      ],
      script: [
        {
          json: {
            "@context": "http://schema.org",
            "@type": "ItemList",
            "itemListElement": this.metadataProducts
          },
          type: 'application/ld+json'
        }
      ]
      }
    },
    data () {
      return {
        loading: true,
        error: false,
        resetInfinityScroll: false,
        supplier: {},
        isNotFound: false
      }
    },
    computed: {
      getMetaTitle () {
        if (!this.isDynamicCategoryPage) return `Browse all products by ${this.getName}`

        return `Shop the ${this.getName} ${this.dynamicCategoryName} Collection | Scout & Nimble`
      },
      getMetaDescription () {
        if (!this.isDynamicCategoryPage) return `Check out all products offered by ${this.getName}`

        return `Transform your space and refine elegance with ${this.getName} ${this.dynamicCategoryName}. ` +
          `Browse an extensive range of ${this.dynamicCategoryName} from ${this.getName}.`
      },
      searchProducts () {
        return this.filtersLoaded
      },
      getName() {
        return this.error ? '' : this.supplier.name
      },
      productsPath () {
        return this.getApiPath(this.$route.fullPath)
      },
      supplierHeader () {
        const { name, banner_description } = this.supplier
        return {
          ...this.supplier,
          name: this.isDynamicCategoryPage ? `${name} ${this.dynamicCategoryName}` : name,
          breadcumb: name,
          short_description: banner_description,
          childrens: []
        }
      },
      defaultFilters () {
        return compact([
          this.categoryDefaultFilters,
          `supplier.slug:${this.slug}`,
        ]).join(' AND ')
      },
      slug () {
        return this.$route.params.name
      },
      productsListingHeaderCategory () {
        return { name: this.supplierHeader.name }
      }
    },
    async fetch () {
      this.$store.dispatch('productData/clearCurrentProduct')
      await this.loadProducts(this.productsPath)
    },
    mounted () {
      this.loadFilters()
      this.loadCategories()
    },
    methods: {
      triggerFunctionsAfterMounting (products) {
        this.updateMetadataProducts(products)

        if (!products.length) { return }

        this.trackEvent('browse', {
          'type': 'brand',
          'name': this.supplier.name,
          'products': products.map(x => x.objectID)
        })
      },
      loadProducts (path) {
        this.loading = process.client

        return ApiProduct.getProducts(this.$axios, path, {}, { category_permalink: this.categoryPermalink })
          .then(response => {
            this.supplier = response.data.supplier
            this.loading = false
          })
          .catch(error => {
            if (error.response.status === 404) {
              this.isNotFound = true
            } else {
              this.error = true
            }

            this.loading = false
          })
          .finally(() => {
            this.$root.$emit('scrollBeforeEnter')
          })
      }
    }
  }

</script>

<style lang="scss" scoped>
  @import '@/assets/stylesheets/variables.scss';
  @import '@/assets/stylesheets/unstylished-headers.scss';

  ::v-deep .sticky-top-bar__background {
    display: none;
  }

  .products {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 20px;

    .brand-header {
      border-bottom: 1px solid #d5d5d5;
      margin-bottom: 20px;

      .breadcumb {
        grid-template-columns: 1fr;
        font-size: 14px;
        line-height: 1.36;
        letter-spacing: 0.34px;
        text-align: left;
        color: $color-gray;

      }

      .title {
        grid-template-columns: 1fr;
        font-family: Tenez;
        font-size: 34px;

        line-height: 1.21;
        letter-spacing: 1.7px;
        text-align: center;
        color: $color-black;
      }
    }

    .product-list {
      display: grid;
      grid-row-gap: 20px;
      grid-auto-rows: min-content;

      .product-options {
        display: grid;
        grid-template-columns: min-content auto;
        grid-column-gap: 20px;
        grid-auto-columns: min-content;
        grid-auto-rows: min-content;
        grid-row: 1;

        .display-select {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          grid-column-gap: 6px;

          div {
            display: inline-block;
            cursor: pointer;
            justify-self: center;
            align-self: center;

            img {
              width: 24px;
            }
          }
        }

        .order-select {
          select {
            padding: 6px 12px;
            border: 1px solid #000;
            border-radius: 2px;
          }
        }
      }

      .product-list-wrapper {
        grid-row: 2;
      }

      .missing-products {
        font-size: 3.2rem;
        font-weight: bold;
        text-transform: capitalize;
        text-align: center;
      }
    }

    .product-list-main {
      display: grid;
      grid-template-columns: 318px 1fr;
      grid-column-gap: 60px;
    }

    @media screen and (max-width: 1330px) {
      .product-list-main {
        grid-template-columns: 270px 1fr;
        grid-column-gap: 40px;
      }
    }

    @media screen and (max-width: $products-filters-mobile-max) {
      .products-listing-header {
        margin-bottom: 0;
      }

      .product-list-main {
        grid-template-columns: 1fr;
        row-gap: 20px;
      }
    }

    @media only screen and (max-width: 992px) {
      grid-template-columns: 1fr;

      .product-list {
        .product-options {
          grid-template-columns: 1fr;

          .display-select {
            display: none;
          }
        }
      }
    }
  }
</style>
